import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  acceptInvitation,
  assessAllInvitations,
  declineInvitation,
  getInvitationHistory,
  getInvitations,
  getInvitationWelcomeCount,
  type InvitationItemDto,
  refineInvitationWelcome,
  serverErrorMessage,
} from '../../lib/api.js';
import { formatRelative } from '../../lib/format.js';
import { LIVE_POLL_MS } from '../../lib/poll.js';
import { useWorkSummary } from '../../lib/use-work-summary.js';
import { HistoryFacets } from '../ui/HistoryFacets.js';
import { JobChip } from '../ui/JobChip.js';
import { HeldWelcomes } from './HeldWelcomes.js';
import { QueuePage, QueueScoreboard, type QueueTab } from './QueuePage.js';

/**
 * R20 — Connection requests ported onto QueuePage (was `InvitationsModal`, deleted).
 * Routed `?view=connection-requests`: received invitations in the (virtualized,
 * 281+) list with verdict chips; the inviter profile + editable welcome + Accept /
 * Decline / Refine in the canvas; Queue|History tabs; bulk bar = DECLINE-ONLY
 * (#1 — no outbound message), pausing honestly at the 20/day cap. Done-today
 * scoreboard.
 */

// Stable empty array so `history.data?.items ?? …` doesn't allocate a new [] each
// render (which would bust the histFiltered useMemo). Wave-2 review fix.
const NO_HISTORY: never[] = [];

type VerdictFilter = 'all' | 'review' | 'accept' | 'decline';
const FILTERS: ReadonlyArray<{ key: VerdictFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'review', label: 'Review' },
  { key: 'accept', label: 'Accept' },
  { key: 'decline', label: 'Decline' },
];

export function InvitationsView() {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tab, setTab] = useState<QueueTab>('queue');
  const [filter, setFilter] = useState<VerdictFilter>('all');

  const feed = useQuery({
    queryKey: ['invitations'],
    queryFn: ({ signal }) => getInvitations({ signal }),
    // R18-21 review — assessment lands as an UPDATE (no INSERT trigger → SSE-silent),
    // so poll fast while any invitation is still unassessed; relax once all have a
    // verdict (matches the keep-in-touch pattern).
    refetchInterval:
      tab === 'queue'
        ? (q) => ((q.state.data?.items ?? []).some((i) => !i.assessed_at) ? 3_000 : LIVE_POLL_MS)
        : false,
  });
  const history = useQuery({
    queryKey: ['invitation-history'],
    queryFn: ({ signal }) => getInvitationHistory({ signal }),
    enabled: tab === 'history',
  });
  // batch-411 - how many welcomes are waiting on the operator (the Welcomes tab badge).
  const welcomeCount = useQuery({
    queryKey: ['invitation-welcome-count'],
    queryFn: ({ signal }) => getInvitationWelcomeCount({ signal }),
    refetchInterval: LIVE_POLL_MS,
  });
  const work = useWorkSummary();
  const sendArmed = Boolean(feed.data?.send_enabled);
  // Wave-2 — client-side facets for the History tab.
  const [histSearch, setHistSearch] = useState('');
  const [histStatus, setHistStatus] = useState<string | null>(null);
  const histRows = history.data?.items ?? NO_HISTORY;
  const histFiltered = useMemo(() => {
    const q = histSearch.trim().toLowerCase();
    return histRows.filter(
      (h) =>
        (!q || (h.sender_name ?? '').toLowerCase().includes(q)) &&
        (!histStatus || h.status === histStatus),
    );
  }, [histRows, histSearch, histStatus]);

  const allItems = feed.data?.items ?? [];
  const items = useMemo(
    () => (filter === 'all' ? allItems : allItems.filter((i) => i.verdict === filter)),
    [allItems, filter],
  );
  const unassessed = useMemo(() => allItems.filter((i) => !i.assessed_at), [allItems]);
  // batch-402 — a verdict made under an older rubric is out of date: it can be read but never
  // auto-acted, and "Assess all" now re-runs it. Counted apart so the label says which is which.
  const outOfDate = useMemo(
    () => allItems.filter((i) => i.policy_status === 'legacy' || i.policy_status === 'changed'),
    [allItems],
  );
  const needsAssessing = unassessed.length + outOfDate.length;
  const declineCandidates = useMemo(
    () => allItems.filter((i) => i.verdict === 'decline'),
    [allItems],
  );

  useEffect(() => {
    if (items.length === 0) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    if (selectedId === null || !items.some((i) => i.id === selectedId)) {
      setSelectedId(items[0]?.id ?? null);
    }
  }, [items, selectedId]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['invitations'] });
  const assessAll = useMutation({
    mutationFn: () => assessAllInvitations(),
    onSettled: invalidate,
  });
  // R20 — DECLINE-ONLY bulk: decline every 'decline'-verdict request, stopping
  // honestly when the server's 20/day cap (or any error) is hit.
  const declineAll = useMutation({
    mutationFn: async () => {
      let declined = 0;
      let stopped: string | null = null;
      for (const i of declineCandidates) {
        try {
          await declineInvitation(i.id);
          declined += 1;
        } catch (e) {
          // R18-21 review — surface WHY it stopped (the 20/day cap or an error)
          // instead of silently declining zero.
          stopped = serverErrorMessage(e, 'stopped');
          break;
        }
      }
      return { declined, stopped };
    },
    onSettled: invalidate,
  });

  return (
    <QueuePage<InvitationItemDto>
      testId="invitations-view"
      title="Connection requests"
      count={items.length}
      items={items}
      getId={(i) => i.id}
      selectedId={selectedId}
      onSelect={setSelectedId}
      rowEstimate={72}
      isLoading={feed.isLoading}
      isError={feed.isError}
      onRetry={() => void feed.refetch()}
      emptyTitle="No connection requests"
      emptyHint="Received LinkedIn invitations appear here once fetched + assessed."
      tab={tab}
      onTabChange={setTab}
      capabilityBadge={
        <span data-testid="inv-capability">
          {sendArmed ? 'accept · decline · welcome' : 'review only'}
        </span>
      }
      filterChips={FILTERS.map((f) => (
        <button
          key={f.key}
          type="button"
          className={filter === f.key ? 'queue-chip active' : 'queue-chip'}
          aria-pressed={filter === f.key}
          onClick={() => setFilter(f.key)}
        >
          {f.label}
        </button>
      ))}
      bulkMenu={
        <>
          {feed.data?.enabled && needsAssessing > 0 ? (
            assessAll.isPending ? (
              <JobChip label="Assessing…" testId="inv-assess-all-chip" />
            ) : (
              <button
                type="button"
                className="unified-overflow-item"
                data-testid="inv-assess-all"
                onClick={() => assessAll.mutate()}
              >
                <span>✨ Assess all ({needsAssessing})</span>
                <span className="unified-overflow-hint">
                  {outOfDate.length > 0
                    ? `${unassessed.length} new · ${outOfDate.length} under an older rubric`
                    : 'Queue the AI verdict for each'}
                </span>
              </button>
            )
          ) : null}
          {sendArmed && declineCandidates.length > 0 ? (
            declineAll.isPending ? (
              <JobChip label="Declining…" testId="inv-decline-all-chip" />
            ) : (
              <button
                type="button"
                className="unified-overflow-item"
                data-testid="inv-decline-all"
                onClick={() => declineAll.mutate()}
              >
                <span>✕ Decline all "decline" ({declineCandidates.length})</span>
                <span className="unified-overflow-hint">
                  No message sent · pauses at the daily cap
                </span>
              </button>
            )
          ) : null}
        </>
      }
      scoreboard={
        <>
          <QueueScoreboard
            openCount={allItems.length}
            doneToday={work.data?.done_today?.invitations ?? 0}
          />
          {declineAll.data ? (
            <span className="jc-hint" data-testid="inv-decline-result">
              {' · '}Declined {declineAll.data.declined}
              {declineAll.data.stopped ? ` · paused: ${declineAll.data.stopped}` : ''}
            </span>
          ) : null}
          {assessAll.isError ? (
            <span className="jc-hint" data-testid="inv-assess-error" role="alert">
              {' · '}Assess-all failed — try again.
            </span>
          ) : null}
        </>
      }
      renderRow={(item) => <InvitationRowSummary item={item} />}
      renderCanvas={(item) =>
        item ? (
          <InvitationCanvas key={item.id} item={item} sendArmed={sendArmed} />
        ) : (
          <p className="queue-canvas-empty">Select a request to review.</p>
        )
      }
      welcomesTab={{
        count: welcomeCount.data?.open ?? 0,
        render: () => <HeldWelcomes sendArmed={sendArmed} />,
      }}
      renderHistory={() =>
        history.isLoading ? (
          <ul className="eng-history">
            <li>Loading…</li>
          </ul>
        ) : histRows.length === 0 ? (
          <ul className="eng-history">
            <li>No decisions yet.</li>
          </ul>
        ) : (
          <>
            <HistoryFacets
              search={histSearch}
              onSearch={setHistSearch}
              shown={histFiltered.length}
              total={histRows.length}
              groups={[
                {
                  label: 'Status',
                  value: histStatus,
                  onChange: setHistStatus,
                  options: [
                    { value: 'done', label: 'Done' },
                    { value: 'failed', label: 'Failed' },
                  ],
                },
              ]}
            />
            <ul className="eng-history">
              {histFiltered.length === 0 ? (
                <li>No matches.</li>
              ) : (
                histFiltered.map((h) => (
                  <li key={h.id}>
                    <span className="jc-kind">{h.decision}</span>
                    <span
                      className={`jc-dot jc-dot-${h.status === 'done' ? 'ok' : h.status === 'failed' ? 'bad' : 'pending'}`}
                    >
                      {h.status}
                    </span>
                    <strong>{h.sender_name ?? '—'}</strong>
                    {h.requested_at ? <time>{formatRelative(h.requested_at)}</time> : null}
                  </li>
                ))
              )}
            </ul>
          </>
        )
      }
    />
  );
}

function verdictDot(v: InvitationItemDto['verdict']): { cls: string; label: string } {
  if (v === 'accept') return { cls: 'ok', label: 'Accept' };
  if (v === 'decline') return { cls: 'bad', label: 'Decline' };
  if (v === 'review') return { cls: 'pending', label: 'Review' };
  return { cls: 'muted', label: 'Unassessed' };
}

function InvitationRowSummary({ item }: { item: InvitationItemDto }) {
  const v = verdictDot(item.verdict);
  return (
    <span className="jc-row">
      <span className="jc-row-headline">{item.sender_name ?? 'A person'}</span>
      {item.sender_headline ? (
        <span className="eng-row-snippet">{item.sender_headline}</span>
      ) : null}
      <span className="jc-row-meta">
        <span className={`jc-dot jc-dot-${v.cls}`}>{v.label}</span>
        {item.sender_company ? <span>🏢 {item.sender_company}</span> : null}
        {item.shared_connections_count ? <span>🔗 {item.shared_connections_count}</span> : null}
      </span>
    </span>
  );
}

function InvitationCanvas({ item, sendArmed }: { item: InvitationItemDto; sendArmed: boolean }) {
  const qc = useQueryClient();
  // R18-21 review (HIGH) — `null` = follow the server welcome (so a Refine re-seeds
  // the editor); a string = the operator's edit. The canvas is keyed by invitation
  // id, so without this the refined welcome never appeared.
  const [welcomeOverride, setWelcomeOverride] = useState<string | null>(null);
  const welcome = welcomeOverride ?? item.welcome_text ?? '';
  const [guidance, setGuidance] = useState('');
  const [err, setErr] = useState<string | null>(null);
  // batch-406 - an accept that stuck but whose welcome DM did not go out must say so.
  const [welcomeNote, setWelcomeNote] = useState<string | null>(null);
  const [refining, setRefining] = useState(false);
  // (welcome,assessed_at) this canvas waits to change away from; Refine shows
  // "Refining…" until the re-assessment lands (the refine re-runs assessment → both
  // fields update when it completes; the canvas is keyed by invitation id).
  const refineClaim = useRef<string | null>(null);
  const [done, setDone] = useState<'accepted' | 'declined' | null>(
    item.status === 'accepted' ? 'accepted' : item.status === 'declined' ? 'declined' : null,
  );
  useEffect(() => {
    const sig = `${item.welcome_text ?? ''}|${item.assessed_at ?? ''}`;
    if (sig !== refineClaim.current) setRefining(false);
  }, [item.welcome_text, item.assessed_at]);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['invitations'] });

  const acceptMut = useMutation({
    mutationFn: () => acceptInvitation(item.id, welcome.trim() || undefined),
    onMutate: () => setErr(null),
    onSuccess: (r) => {
      setDone('accepted');
      setWelcomeNote(
        r.welcome_error
          ? `Accepted, but the welcome message was NOT sent (${r.welcome_error}). Message them yourself if you want to.`
          : null,
      );
      void invalidate();
    },
    onError: (e) => setErr(serverErrorMessage(e, 'accept failed')),
  });
  const declineMut = useMutation({
    mutationFn: () => declineInvitation(item.id),
    onSuccess: () => {
      setDone('declined');
      void invalidate();
    },
    onError: (e) => setErr(serverErrorMessage(e, 'decline failed')),
  });
  const refineMut = useMutation({
    mutationFn: () => refineInvitationWelcome(item.id, guidance.trim()),
    onMutate: () => {
      setErr(null);
      refineClaim.current = `${item.welcome_text ?? ''}|${item.assessed_at ?? ''}`;
      setRefining(true);
    },
    onSuccess: () => {
      setGuidance('');
      setWelcomeOverride(null); // follow the refreshed server welcome when it lands
      void invalidate();
    },
    onError: (e) => {
      setRefining(false);
      setErr(serverErrorMessage(e, 'refine failed'));
    },
  });

  const profileUrl = item.sender_public_identifier
    ? `https://www.linkedin.com/in/${item.sender_public_identifier}`
    : null;

  return (
    <div className="jc-canvas" data-testid="inv-canvas">
      <div className="jc-canvas-head">
        <h2>{item.sender_name ?? 'A person'}</h2>
        {profileUrl ? (
          <a className="jc-btn" href={profileUrl} target="_blank" rel="noreferrer">
            Profile
          </a>
        ) : null}
      </div>
      {item.sender_headline ? <p className="jc-hint">{item.sender_headline}</p> : null}
      <p className="jc-row-meta">
        {item.sender_company ? <span>🏢 {item.sender_company}</span> : null}
        {item.sender_location ? <span>📍 {item.sender_location}</span> : null}
        {item.shared_connections_count ? (
          <span>🔗 {item.shared_connections_count} mutual</span>
        ) : null}
      </p>
      {item.message ? <p className="eng-post-text">“{item.message}”</p> : null}
      {item.reason ? (
        <p className="jc-hint">
          AI verdict <strong>{item.ai_verdict ?? item.verdict ?? '—'}</strong>: {item.reason}
        </p>
      ) : null}
      {item.mutual_gate?.note ? (
        <p className="jc-hint" data-testid="inv-mutual-gate">
          {item.mutual_gate.note}
        </p>
      ) : null}

      <label className="jc-hint" htmlFor="inv-welcome">
        Welcome message (sent on accept)
      </label>
      <textarea
        id="inv-welcome"
        value={welcome}
        onChange={(e) => setWelcomeOverride(e.target.value)}
        rows={3}
        className="jc-draft"
        aria-label="Welcome message"
      />

      {done ? (
        <span className="jc-sent" data-testid="inv-done">
          {done === 'accepted' ? 'Accepted ✓' : 'Declined ✓'}
        </span>
      ) : (
        <div className="jc-actions">
          {sendArmed ? (
            <>
              <button
                type="button"
                className="jc-btn jc-send"
                data-testid="inv-accept"
                onClick={() => acceptMut.mutate()}
                disabled={acceptMut.isPending || refining}
              >
                {acceptMut.isPending ? 'Accepting…' : 'Accept + welcome'}
              </button>
              <button
                type="button"
                className="jc-btn"
                data-testid="inv-decline"
                onClick={() => declineMut.mutate()}
                disabled={declineMut.isPending || refining}
              >
                Decline
              </button>
              {refining ? (
                <span className="jc-hint" data-testid="inv-refining">
                  Refining…
                </span>
              ) : null}
            </>
          ) : (
            <span className="jc-hint">Sending is not enabled — review only.</span>
          )}
        </div>
      )}

      <div className="kit-snooze">
        <input
          type="text"
          className="queue-search"
          placeholder="Guidance for the AI welcome…"
          value={guidance}
          onChange={(e) => setGuidance(e.target.value)}
          aria-label="Welcome guidance"
        />
        <button
          type="button"
          className="jc-btn"
          data-testid="inv-refine"
          onClick={() => refineMut.mutate()}
          disabled={refineMut.isPending || refining || !guidance.trim()}
        >
          {refining ? 'Refining…' : '✨ Refine'}
        </button>
      </div>
      {err ? <p className="jc-failed">{err}</p> : null}
      {welcomeNote ? (
        <p className="jc-failed" data-testid="inv-welcome-failed">
          {welcomeNote}
        </p>
      ) : null}
    </div>
  );
}
